import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SEO } from "../components/SEO";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ShoppingCart, Tag, Check, Loader2 } from "lucide-react";
import { API, GARMENT_TYPES, PRINT_LOCATIONS } from "../constants";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const navigate = useNavigate();

  // Save cart snapshot for abandoned cart recovery
  const saveCartSnapshot = useCallback(async () => {
    if (customerInfo.email && cartItems.length > 0) {
      try {
        await axios.post(`${API}/email-flows/cart-snapshot`, {
          email: customerInfo.email,
          name: customerInfo.name,
          items: cartItems.map(i => ({ product_id: i.product_id, product_name: i.product_name, quantity: i.quantity, price: i.price })),
          total: cartItems.reduce((sum, i) => sum + (i.price * i.quantity), 0),
        });
      } catch (e) { /* silent */ }
    }
  }, [customerInfo.email, customerInfo.name, cartItems]);

  useEffect(() => {
    loadCart();
  }, []);

  // Debounce abandoned cart snapshot
  useEffect(() => {
    if (!customerInfo.email) return;
    const timer = setTimeout(saveCartSnapshot, 2000);
    return () => clearTimeout(timer);
  }, [customerInfo.email, saveCartSnapshot]);

  const loadCart = () => {
    const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItems(savedCart);
  };

  const removeFromCart = (itemId) => {
    const updatedCart = cartItems.filter(item => item.id !== itemId);
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    toast.success('Item removed from cart');
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedCart = cartItems.map(item => {
      if (item.id === itemId) {
        const newPrice = item.unit_price * newQuantity;
        return { ...item, quantity: newQuantity, price: newPrice };
      }
      return item;
    });
    
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
  };

  const getTotalAmount = () => {
    const subtotal = cartItems.reduce((total, item) => total + item.price, 0);
    if (couponResult?.discount) {
      return Math.max(0, subtotal - couponResult.discount);
    }
    return subtotal;
  };

  const getSubtotal = () => {
    return cartItems.reduce((total, item) => total + item.price, 0);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    setCouponError('');
    setCouponResult(null);
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        email: customerInfo.email || '',
        order_total: getSubtotal(),
      });
      setCouponResult(data);
      toast.success(`Coupon applied: ${data.label}`);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
      setCouponResult(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponResult(null);
    setCouponError('');
  };

  const handleCheckout = async () => {
    if (!customerInfo.name || !customerInfo.email) {
      toast.error('Please fill in your contact information');
      return;
    }

    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      // Determine workflow type based on products in cart
      const hasLargeFormat = cartItems.some(item => item.product_id === 'large_format');
      const workflowType = hasLargeFormat ? 'large_format' : 'apparel';

      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          garment_type: item.garment_type,
          print_locations: item.print_locations,
          colors: item.colors,
          embroidery_size_inches: item.embroidery_size_inches,
          is_names_only: item.is_names_only,
          names_list: item.names_list,
          sticker_width: item.sticker_width,
          sticker_height: item.sticker_height,
          cut_type: item.cut_type,
          print_width: item.print_width,
          print_height: item.print_height,
          print_category: item.print_category,
          print_product: item.print_product,
          filename: item.filename,
          file_path: item.file_path,
          price: item.price
        })),
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone || null,
        workflow_type: workflowType,
        payment_method: paymentMethod
      };

      // Use the new unified checkout endpoint
      const checkoutResponse = await axios.post(`${API}/checkout`, orderData);
      
      if (checkoutResponse.data.checkout_url) {
        // Clear cart and redirect to payment
        localStorage.removeItem('cart');
        window.location.href = checkoutResponse.data.checkout_url;
      } else if (paymentMethod === 'zelle') {
        // Zelle - manual payment instructions
        localStorage.removeItem('cart');
        navigate(`/order-confirmation/${checkoutResponse.data.order_id}?payment_method=zelle`);
      } else if (paymentMethod === 'ach') {
        // ACH transfer - can use Stripe ACH or Plaid
        const achResponse = await axios.post(
          `${API}/checkout/ach?order_id=${order.id}&origin_url=${encodeURIComponent(origin)}`
        );
        localStorage.removeItem('cart');
        window.location.href = achResponse.data.url;
      }

    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error('Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getItemDisplayText = (item) => {
    let details = [];
    
    // Only show garment type for products that need garments
    if (item.garment_type && !['stickers', 'large_format'].includes(item.product_id)) {
      details.push(GARMENT_TYPES[item.garment_type]?.name);
    }
    
    if (item.print_locations && item.print_locations.length > 0) {
      details.push(`Print: ${item.print_locations.map(loc => PRINT_LOCATIONS[loc]?.name).join(', ')}`);
    }
    
    if (item.colors) {
      details.push(`${item.colors} color${item.colors > 1 ? 's' : ''}`);
    }
    
    if (item.embroidery_size_inches) {
      details.push(`${item.embroidery_size_inches} sq in (${Math.round(item.embroidery_size_inches * 1000).toLocaleString()} stitches)`);
    }
    
    if (item.is_names_only) {
      details.push('Names Only');
    }
    
    if (item.art_fee > 0) {
      details.push(`Art fee: $${item.art_fee}`);
    }
    
    if (item.product_id === 'fastisol_transfers') {
      details.push('DIY Heat Transfers');
    }
    
    return details.join(' • ');
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-12">
      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">Checkout</span>
      <h1 className="font-archivo text-4xl uppercase tracking-tight text-ink mb-8">Shopping Cart</h1>

      {cartItems.length === 0 ? (
        <div className="text-center py-12 border-2 border-ink bg-bone">
          <ShoppingCart className="mx-auto h-24 w-24 text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Configure your printing project to get started!</p>
          <Button onClick={() => navigate('/products')} data-testid="continue-shopping-btn">
            Browse Services
          </Button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <Card key={item.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">{item.product_name}</h3>
                    <p className="text-gray-600 text-sm mt-1">{getItemDisplayText(item)}</p>
                    {item.filename && (
                      <p className="text-sm text-green-600 mt-1">
                        📎 {item.filename}
                      </p>
                    )}
                    {item.quality_status && (
                      <Badge className={`mt-2 text-xs ${
                        item.quality_status === 'excellent' ? 'bg-green-100 text-green-800' :
                        item.quality_status === 'auto_vectorizable' ? 'bg-blue-100 text-blue-800' :
                        item.quality_status === 'needs_work' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.quality_status === 'excellent' ? 'Perfect Quality' :
                         item.quality_status === 'auto_vectorizable' ? 'Auto-Vectorized' :
                         item.quality_status === 'needs_work' ? 'Enhanced' : 'Reviewed'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 ml-4">
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                      className="w-20"
                      data-testid={`quantity-${item.id}`}
                    />
                    <div className="text-right">
                      <div className="text-lg font-semibold">${item.price.toFixed(2)}</div>
                      <div className="text-sm text-gray-500">${item.unit_price.toFixed(2)} each</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeFromCart(item.id)}
                      data-testid={`remove-${item.id}`}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <h2 className="text-2xl font-semibold mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div>
                  <Label htmlFor="customer-name">Full Name</Label>
                  <Input
                    id="customer-name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                    placeholder="Enter your full name"
                    data-testid="customer-name"
                  />
                </div>
                <div>
                  <Label htmlFor="customer-email">Email Address</Label>
                  <Input
                    id="customer-email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                    placeholder="Enter your email"
                    data-testid="customer-email"
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                {/* Coupon Code */}
                <div data-testid="coupon-section">
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Promo Code</Label>
                  {couponResult ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        <div>
                          <p className="text-sm font-semibold text-green-800">{couponResult.code}</p>
                          <p className="text-xs text-green-600">{couponResult.label}{couponResult.free_item ? ` — ${couponResult.free_item}` : ''}</p>
                        </div>
                      </div>
                      <button onClick={removeCoupon} className="text-xs text-green-600 hover:text-green-800 underline" data-testid="remove-coupon-btn">Remove</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                        placeholder="Enter promo code"
                        className="flex-1"
                        data-testid="coupon-input"
                        onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                      />
                      <Button
                        variant="outline"
                        onClick={applyCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                        className="shrink-0"
                        data-testid="apply-coupon-btn"
                      >
                        {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4 mr-1" />}
                        Apply
                      </Button>
                    </div>
                  )}
                  {couponError && <p className="text-xs text-red-500 mt-1" data-testid="coupon-error">{couponError}</p>}
                </div>

                {/* Totals */}
                {couponResult?.discount > 0 && (
                  <>
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>Subtotal:</span>
                      <span>${getSubtotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-green-600 font-medium">
                      <span>Discount ({couponResult.label}):</span>
                      <span>-${couponResult.discount.toFixed(2)}</span>
                    </div>
                  </>
                )}
                {couponResult?.type === 'free_item' && (
                  <div className="flex justify-between items-center text-sm text-green-600 font-medium">
                    <span>Free item included:</span>
                    <span>{couponResult.free_item}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Total:</span>
                  <span>${getTotalAmount().toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="mt-6 space-y-4">
                <Label className="text-lg font-semibold">Choose Payment Method</Label>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer" data-testid="payment-paypal">
                    <input
                      type="radio"
                      id="paypal"
                      name="payment-method"
                      value="paypal"
                      checked={paymentMethod === 'paypal'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="paypal" className="flex items-center space-x-2 cursor-pointer">
                      <span className="text-blue-500">🅿️</span>
                      <span className="font-medium">PayPal / Credit / Debit Card</span>
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer" data-testid="payment-zelle">
                    <input
                      type="radio"
                      id="zelle"
                      name="payment-method"
                      value="zelle"
                      checked={paymentMethod === 'zelle'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="zelle" className="flex items-center space-x-2 cursor-pointer">
                      <span className="text-purple-600">💰</span>
                      <span className="font-medium">Zelle (Bank Transfer)</span>
                    </label>
                  </div>
                </div>
                
                {paymentMethod === 'zelle' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                    <p className="text-purple-800">
                      <strong>Zelle Instructions:</strong> After placing your order, you'll receive Zelle payment instructions via email. 
                      Your order will be processed once payment is confirmed.
                    </p>
                  </div>
                )}
              </div>

              <Button
                size="lg"
                className="w-full mt-6 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                onClick={handleCheckout}
                disabled={loading}
                data-testid="checkout-btn"
              >
                {loading ? 'Processing...' : `Pay with ${paymentMethod === 'paypal' ? 'PayPal' : 'Zelle'} - $${getTotalAmount().toFixed(2)}`}
              </Button>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

// Other components (RequestCall, OrderConfirmation, DesignServices, AdminDashboard) remain the same...
// [Include without changes from previous version]

// Request Call Page

export { Cart };
export default Cart;
